import cx from 'classnames';
import {find} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, IconSize} from 'components';
import {SessionActionControl} from 'components/SessionActionControl';
import {SessionTextChat, SessionTextChatProps} from 'components/SessionTextChat';
import {usePrevious} from 'hooks';
import {Session, AnyParticipant, SessionActivityTextChatDataState, SessionActivityName} from 'interfaces/session';
import {TRANSLATION_KEYS} from 'locale';
import {TextChatMessage, TextChatThreads} from 'types';

import './styles.scss';

type Props = {
    TextChatComponent: React.ComponentType<SessionTextChatProps>;
    expandable: boolean;
    threads?: TextChatThreads;
    activityName?: SessionActivityName;
    hideUnreadMessagesIcon?: boolean;
    disableNewMessageSound?: boolean;
    downloadFileDisabled?: boolean;
    uploadFileHidden?: boolean;
    session: Session<AnyParticipant>;
    iconSize?: IconSize;
    onFileDownload?: (fileName: string, activityId: string) => Promise<any>;
    onSubmitMessage: (message: Partial<TextChatMessage>) => Promise<void>;
    onChangeTextChatActivityState?: (value: SessionActivityTextChatDataState) => void;
    onNewUnreadMessage: () => void;
};

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.SESSION_TEXT_CHAT;

export const TextChat: React.FC<Props> = ({
    TextChatComponent = SessionTextChat, session, threads, activityName, onSubmitMessage, onFileDownload, expandable,
    onChangeTextChatActivityState, onNewUnreadMessage, hideUnreadMessagesIcon, disableNewMessageSound, iconSize = '4xl',
    downloadFileDisabled, uploadFileHidden
}: Props) => {
    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState<boolean>(expandable);
    const [hasUnreadMessages, setHasUnreadMessages] = useState<boolean>(false);
    const inWidget = useMemo(() => Boolean(session.localParticipant.data?.inWidget), [session]);
    const audioVideoActivity = useMemo(() => find(session.activities, {name: SessionActivityName.AudioVideo}), [session]);
    const previousAudioVideo = usePrevious(audioVideoActivity);

    const coBrowsingActivity = useMemo(() => find(session.activities, {name: SessionActivityName.CoBrowsing}), [session]);
    const previousCoBrowsing = usePrevious(coBrowsingActivity);

    const screenSharingActivity = useMemo(() => find(session.activities, {name: SessionActivityName.ScreenSharing}), [session]);
    const previousScreenSharing = usePrevious(screenSharingActivity);

    const onNewMessage = useCallback(() => {
        if (!isOpen) {
            setHasUnreadMessages(true);
            onNewUnreadMessage();
        }
    }, [isOpen, onNewUnreadMessage]);

    useEffect(() => {
        if (hasUnreadMessages && isOpen) {
            setHasUnreadMessages(false);
        }
    }, [hasUnreadMessages, isOpen]);

    useEffect(() => {
        if (inWidget) {
            const audioVideoStarted = audioVideoActivity && !previousAudioVideo;
            const coBrowsingStarted = coBrowsingActivity && !previousCoBrowsing;
            const screenSharingStarted = screenSharingActivity && !previousScreenSharing;
            if (audioVideoStarted || coBrowsingStarted || screenSharingStarted) {
                setIsOpen(false);
            }
        }
    }, [inWidget, audioVideoActivity, previousAudioVideo, coBrowsingActivity, previousCoBrowsing,
        screenSharingActivity, previousScreenSharing, setIsOpen]);

    return (
        <div className='vs-text-chat'>
            {!isOpen &&
                <SessionActionControl
                    label={t(T_KEYS.TOGGLE_ICON_LABEL)}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <Icon
                        name={hasUnreadMessages && !hideUnreadMessagesIcon ? 'chat-highlighted-unread' : 'chat-highlighted'}
                        size={iconSize}
                    />
                </SessionActionControl>}
            <div className={cx('container', {
                opened: isOpen
            })}
            >
                <div className='modal-actions'>
                    <Icon name='close' className='close' button margin={false} onClick={() => setIsOpen(false)} />
                </div>
                <TextChatComponent
                    session={session}
                    threads={threads}
                    activityName={activityName}
                    disableNewMessageSound={disableNewMessageSound}
                    downloadFileDisabled={downloadFileDisabled}
                    uploadFileHidden={uploadFileHidden}
                    onNewMessage={onNewMessage}
                    onFileDownload={onFileDownload}
                    onSubmitMessage={onSubmitMessage}
                    onChangeState={onChangeTextChatActivityState}
                />
            </div>
        </div>
    );
};